import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Switch,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Grid,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ComputerIcon from "@mui/icons-material/Computer";
import PersonIcon from "@mui/icons-material/Person";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import RefreshIcon from "@mui/icons-material/Refresh";
import API from "../../utils/Axios.utils";

const DEFAULT_STAGES = {
  L1_Round: {
    send_email: false,
    mode: "voice_bot",
    display_name: "L1 Round (Technical / Voice Screening)",
    description: "Handled by AHR Voice Bot. Keep OFF to prevent live calendar slot booking.",
  },
  L2_Round: {
    send_email: false,
    mode: "simulation",
    display_name: "L2 Round (Architecture / Simulation Assessment)",
    description: "Online simulation assessment. Platform credentials are sent via recruitment flow.",
  },
  L3_Round: {
    send_email: true,
    mode: "human_interview",
    display_name: "L3 Round (Final Human Interview)",
    description: "Live one-on-one interview. System automatically sends Nylas calendar slot booking link.",
  },
  Awaiting_for_slots: {
    send_email: true,
    mode: "notification",
    display_name: "Awaiting for Slots",
    description: 'Sends candidate "Next Steps: Interview Scheduling in Progress" notification.',
  },
  Interview_Cancelled: {
    send_email: true,
    mode: "notification",
    display_name: "Interview Cancelled",
    description: 'Sends candidate "Interview Cancelled" notification.',
  },
};

const MODE_TAGS = {
  voice_bot: { label: "Voice Bot Screening", color: "#F59E0B", icon: <SmartToyIcon fontSize="inherit" /> },
  simulation: { label: "Training Simulation", color: "#10B981", icon: <ComputerIcon fontSize="inherit" /> },
  human_interview: { label: "Live Human Interview", color: "#3B82F6", icon: <PersonIcon fontSize="inherit" /> },
  notification: { label: "Candidate Notification", color: "#6366F1", icon: <NotificationsActiveIcon fontSize="inherit" /> },
};

const InterviewAutomationConfig = () => {
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await API.get("/api/config/interview-stages");
      if (res?.data?.data?.stages) {
        setStages((prev) => ({
          ...prev,
          ...res.data.data.stages,
        }));
      }
    } catch (err) {
      console.warn("Could not load interview stages config, using defaults:", err);
      // Fallback to default stages without crashing
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (stageKey) => {
    setStages((prev) => ({
      ...prev,
      [stageKey]: {
        ...prev[stageKey],
        send_email: !prev[stageKey]?.send_email,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.post("/api/config/interview-stages", { stages });
      setSnackbar({
        open: true,
        message: "Interview stage automation settings saved successfully!",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to save configuration.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    setStages(DEFAULT_STAGES);
    setSnackbar({
      open: true,
      message: "Reset to recommended defaults (L1=Off, L2=Off, L3=On). Click Save to apply.",
      severity: "info",
    });
  };

  if (loading) {
    return (
      <Paper elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 4, textAlign: "center" }}>
        <CircularProgress size={36} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading interview stage configurations...
        </Typography>
      </Paper>
    );
  }

  const interviewRounds = ["L1_Round", "L2_Round", "L3_Round"];
  const notificationStages = ["Awaiting_for_slots", "Interview_Cancelled"];

  const renderStageCard = (stageKey) => {
    const stage = stages[stageKey] || DEFAULT_STAGES[stageKey] || {
      display_name: stageKey.replace(/_/g, " "),
      send_email: false,
      mode: "notification",
      description: "",
    };

    const isEnabled = Boolean(stage.send_email);
    const modeMeta = MODE_TAGS[stage.mode] || MODE_TAGS.notification;

    return (
      <Paper
        key={stageKey}
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${isEnabled ? "#BFDBFE" : "#E5E7EB"}`,
          backgroundColor: isEnabled ? "#F8FAFC" : "#FFFFFF",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            borderColor: isEnabled ? "#93C5FD" : "#D1D5DB",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 0.75 }}>
              <Typography variant="subtitle1" fontWeight={600} color="#1E293B">
                {stage.display_name}
              </Typography>
              <Chip
                icon={modeMeta.icon}
                label={modeMeta.label}
                size="small"
                sx={{
                  backgroundColor: `${modeMeta.color}15`,
                  color: modeMeta.color,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  border: `1px solid ${modeMeta.color}40`,
                }}
              />
              <Chip
                label={isEnabled ? "Email Active" : "Email Bypassed"}
                size="small"
                sx={{
                  backgroundColor: isEnabled ? "#DCFCE7" : "#F1F5F9",
                  color: isEnabled ? "#15803D" : "#64748B",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            </Box>
            <Typography variant="body2" color="#64748B" sx={{ lineHeight: 1.5 }}>
              {stage.description}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 90 }}>
            <Switch
              checked={isEnabled}
              onChange={() => handleToggle(stageKey)}
              color="primary"
            />
            <Typography variant="caption" sx={{ color: isEnabled ? "#2563EB" : "#94A3B8", fontWeight: 600, mt: -0.5 }}>
              {isEnabled ? "AUTOMATED" : "BYPASSED"}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#FFFFFF",
        }}
      >
        {/* Header & Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2, mb: 3 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <RecordVoiceOverIcon sx={{ color: "#2563EB", fontSize: 26 }} />
              <Typography variant="h6" fontWeight={700} color="#1E293B">
                Interview Stage Automation
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 680 }}>
              Control which interview rounds automatically email candidates with Nylas slot booking links, and which are bypassed for external bot integrations (AHR Voice Bot & Training Simulation).
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleResetDefaults}
              sx={{ textTransform: "none", color: "#64748B", borderColor: "#CBD5E1" }}
            >
              Reset Defaults
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "#1E40AF",
                "&:hover": { backgroundColor: "#1D4ED8" },
                px: 2.5,
              }}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3, borderRadius: 1.5, fontSize: "0.85rem" }}>
          <strong>Voice Bot & Simulation Flow:</strong> Keep <strong>L1 Round</strong> and <strong>L2 Round</strong> toggled <strong>OFF</strong> so external bots can conduct calls and send simulation credentials without sending human interview slot links. Keep <strong>L3 Round</strong> toggled <strong>ON</strong> for live interviews.
        </Alert>

        {/* Section 1: Live Interview Rounds */}
        <Typography variant="subtitle2" fontWeight={700} color="#334155" sx={{ textTransform: "uppercase", letterSpacing: "0.5px", mb: 1.5 }}>
          Interview Rounds (Nylas Slot Booking)
        </Typography>
        {interviewRounds.map(renderStageCard)}

        <Divider sx={{ my: 3 }} />

        {/* Section 2: Pre & Post Interview Status Updates */}
        <Typography variant="subtitle2" fontWeight={700} color="#334155" sx={{ textTransform: "uppercase", letterSpacing: "0.5px", mb: 1.5 }}>
          Pre / Post Interview Candidate Notifications
        </Typography>
        {notificationStages.map(renderStageCard)}
      </Paper>

      {/* Snackbar notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} sx={{ boxShadow: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InterviewAutomationConfig;
